import { status as AppointmentStatuses } from '@spike/appointment-model';
import { NewBookingsStatus, saveCreateAppointmentThunk } from '@spike/new-bookings-action';
import { Order } from '@spike/order-model';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import { useApiClientWrapper, useAuth, useMarketplace } from 'hooks';
import { NewAppointment } from 'model/NewBooking';
import {
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    versionPetAndServices
} from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { convertDiscount, getUsedPetIds } from './EditOrderItem';
import { getOrderStartTime } from 'components/Checkout2/utils/OrderUtils';

export interface AddPetProps {
    order: Order;
    bookingId: number;
    className?: string;
    onClose: () => void;
}

export const AddPet: FunctionComponent<AddPetProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const marketplace = useMarketplace();
    const auth = useAuth();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const [saving, setSaving] = useState(false);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderItemSuccess:
            case OrdersStatus.ReloadSuccess:
                setSaving(false);
                props.onClose();
                break;
            case OrdersStatus.Error:
                setSaving(false);
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingCreateAppointmentSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case NewBookingsStatus.Error:
                setSaving(false);
        }
    }, [newBookingsStatus]);

    const saveHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        setSaving(true);
        if (date && petId && service && auth.user && props.order.client.id) {
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: date.clone(),
                customer_id: props.order.client.id,
                pet_id: petId,
                service_id: service?.service.id,
                staff_id: staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: apiClientWrapper.uuid(),
                created_by: auth.user.staffId,
                discount_amount: discount?.type === 'amount' ? discount?.value.toFixed(2) : undefined,
                discount_percentage: discount?.type === 'percentage' ? discount?.value : undefined,
                order_item_price: service.price
            };

            dispatch(saveCreateAppointmentThunk(apiClientWrapper, newAppointment));
        }
    };

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order]);

    const bookAt = getOrderStartTime(props.order) ?? moment();

    return (
        <PetAndServices
            parentId={props.order.client.id!}
            date={bookAt}
            versionPetAndServices={versionPetAndServices.another}
            petsUsed={usedPetIds}
            discount={convertDiscount()}
            loading={saving}
            onBack={props.onClose}
            onBook={saveHandler}
        />
    );
};

export default AddPet;
